import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  FaSyncAlt,
  FaPrint,
  FaWallet,
  FaArrowDown,
  FaArrowUp,
  FaCalendarAlt,
  FaEdit,
} from "react-icons/fa";
import "./AdminThirdPartyLedger.css";
import { Link } from "react-router-dom";

export const PARTY_API_URL = `https://${process.env.REACT_APP_THIRD_PARTY_API_URL}.execute-api.ap-south-1.amazonaws.com/default/thirdPartyUpdate`;
const ADMIN_API_URL = `https://${process.env.REACT_APP_API_KEY_TRANS}.execute-api.ap-south-1.amazonaws.com/default/branchTransaction`;

export default function AdminThirdPartyLedger() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedParty, setSelectedParty] = useState("ALL"); // Default to ALL
  const [party, setParty] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [commissionInput, setCommissionInput] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTx, setNewTx] = useState({
    amount: "",
    commission: "0",
    type: "IN", // IN or OUT
    remarks: "",
  });
  // Date Range States
  const IST_TODAY = new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(IST_TODAY);
  const [endDate, setEndDate] = useState(IST_TODAY);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(ADMIN_API_URL);
      const data = await res.json();

      // Extract transactions and filter out "REJECTED" status
      const allTransactions = data.transactions || [];
      const filteredTransactions = allTransactions.filter(
        (tx) => tx.status !== "REJECTED" && tx.fromBranch !== "LAK",
      );

      setTransactions(filteredTransactions);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      // Small delay to ensure smooth loading state transition
      setTimeout(() => setLoading(false), 500);
    }
  }, []);

  const fetchParty = useCallback(async () => {
    try {
      const res = await fetch(PARTY_API_URL);
      const data = await res.json();
      setParty(data.branches || []);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  }, []);

  useEffect(() => {
    fetchParty();
    fetchTransactions();
  }, [fetchParty, fetchTransactions]);

  const handlePrint = () => window.print();

  const handleCommissionUpdate = async (e, i, b) => {
    e.preventDefault();
    try {
      await fetch(ADMIN_API_URL, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentId: i,
          paymentType: b,
          tpCommission: parseFloat(commissionInput),
          editingBranch: "ADMIN",
        }),
      });
      fetchTransactions();
    } catch (err) {
      console.error("Commission update error:", err);
    }
  };

  const ledgerData = useMemo(() => {
    const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

    const partyTx = transactions.filter((tx) =>
      selectedParty === "ALL"
        ? tx.thirdParty && tx.thirdParty !== "NONE"
        : tx.thirdParty === selectedParty,
    );

    let openingBalance = 0;
    const inRows = [];
    const outRows = [];

    partyTx.forEach((tx) => {
      const amount = Number(tx.amount || 0);
      const tpComm = Number(tx.tpCommission || 0); // admin-set TP commission only
      const isIn = tx.paymentType.includes("IN");
      const txDate = tx.timestamp
        ? new Date(new Date(tx.timestamp).getTime() + IST_OFFSET_MS).toISOString().split("T")[0]
        : null;

      // --- 1. HISTORY LOGIC (Opening Balance) ---
      if (txDate && txDate < startDate) {
        openingBalance += isIn ? amount : -amount;
        openingBalance -= tpComm;
      }

      // --- 2. CURRENT RANGE LOGIC ---
      else if (txDate && txDate >= startDate && txDate <= endDate) {
        const rowData = {
          branch: tx.fromBranch,
          amount,
          comm: tpComm,
          date: tx.timestamp,
          transactionId: tx.paymentId,
          paymentType: tx.paymentType,
        };
        if (isIn) inRows.push(rowData);
        else outRows.push(rowData);
      }
    });

    const totalInAmt = inRows.reduce((a, b) => a + b.amount, 0);
    const totalInComm = inRows.reduce((a, b) => a + b.comm, 0);
    const totalOutAmt = outRows.reduce((a, b) => a + b.amount, 0);
    const totalOutComm = outRows.reduce((a, b) => a + b.comm, 0);
    const totalCommLess = totalInComm + totalOutComm;

    // The finalBalance formula remains the same, but now the starting
    // openingBalance is already "Commission Adjusted"
    const finalBalance =
      openingBalance + totalInAmt - (totalOutAmt + totalCommLess);

    return {
      inRows,
      outRows,
      openingBalance,
      totalInAmt,
      totalInComm,
      totalOutAmt,
      totalOutComm,
      totalCommLess,
      finalBalance,
    };
  }, [transactions, selectedParty, startDate, endDate]);

  const maxRows = Math.max(
    ledgerData.inRows.length,
    ledgerData.outRows.length,
    1,
  );
  const rowIndices = Array.from({ length: maxRows }, (_, i) => i);

  const handleAddDirectTx = async (e) => {
    e.preventDefault();

    if (!selectedParty || selectedParty === "ALL")
      return alert("Select a party.");

    setLoading(true);
    try {
      const txData = {
        paymentId: `OP-${Date.now()}-${selectedParty}`,
        paymentType: newTx.type, // "IN" or "OUT"
        amount: Number(newTx.amount || 0),
        tpCommission: Number(newTx.commission || 0),
        thirdParty: selectedParty,
        fromBranch: "ADMIN",
        toBranch: "",
        flowRole: newTx.type === "IN" ? "RECEIVER" : "SENDER",
        status: "DELIVERED",
        timestamp: new Date().toISOString(),
        remarks: newTx.remarks || "Opening Balance",
      };

      // WRAP IT IN AN OBJECT WITH A 'transactions' KEY
      const payload = {
        transactions: [txData],
      };

      const res = await fetch(ADMIN_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setShowAddForm(false);
        setNewTx({ amount: "", commission: "0", type: "IN", remarks: "" });
        fetchTransactions();
        alert("Updated Successfully");
      } else {
        alert("Failed to update. Check API.");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="admin-tp-page">
      <div className="ledger-card">
        <div className="ledger-header-actions no-print">
          <div className="left-meta">
            <FaWallet className="icon-main" />
            <div>
              <h1>Third Party Statement</h1>
              <div className="date-display">
                <FaCalendarAlt /> {new Date(startDate).toLocaleDateString()} -{" "}
                {new Date(endDate).toLocaleDateString()}
              </div>
            </div>
          </div>

          <div className="right-controls">
            <Link to="/" className="Branch_Back_Btn">
              Back
            </Link>
            <div className="date-inputs">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span>to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <select
              className="party-dropdown"
              value={selectedParty}
              onChange={(e) => {
                setSelectedParty(e.target.value);
                setEditingId(null);
              }}
            >
              <option value="ALL">ALL PARTIES (Combined)</option>
              {party.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.partyName}
                </option>
              ))}
            </select>
            <button
              className="btn-add-direct"
              onClick={() => setShowAddForm(!showAddForm)}
              disabled={selectedParty === "ALL"}
            >
              {showAddForm ? "Cancel" : "+ Add Entry"}
            </button>
            <button
              onClick={fetchTransactions}
              className="btn-icon"
              disabled={loading}
            >
              <FaSyncAlt className={loading ? "spin" : ""} />
            </button>
            <button onClick={handlePrint} className="btn-icon print">
              <FaPrint />
            </button>
          </div>
        </div>

        <div className={`ledger-content-area ${loading ? "is-loading" : ""}`}>
          <div className="print-header">
            <h2>
              {selectedParty === "ALL"
                ? "ALL THIRD PARTIES SUMMARY"
                : `THIRD PARTY A/C - ${selectedParty}`}
            </h2>
            <p>
              Period: {startDate} to {endDate}
            </p>
          </div>
          {showAddForm && (
            <div className="direct-add-form-container no-print">
              <form onSubmit={handleAddDirectTx} className="direct-add-form">
                <select
                  value={newTx.type}
                  onChange={(e) => setNewTx({ ...newTx, type: e.target.value })}
                >
                  <option value="IN">Money IN (To Party)</option>
                  <option value="OUT">Money OUT (From Party)</option>
                </select>
                <input
                  type="number"
                  placeholder="Amount"
                  required
                  value={newTx.amount}
                  onChange={(e) =>
                    setNewTx({ ...newTx, amount: e.target.value })
                  }
                />
                <input
                  type="number"
                  placeholder="Commission"
                  value={newTx.commission}
                  onChange={(e) =>
                    setNewTx({ ...newTx, commission: e.target.value })
                  }
                />
                <input
                  type="text"
                  placeholder="Remarks"
                  value={newTx.remarks}
                  onChange={(e) =>
                    setNewTx({ ...newTx, remarks: e.target.value })
                  }
                />
                <button type="submit" className="submit-btn">
                  Save Entry
                </button>
              </form>
            </div>
          )}
          <table className="ledger-t-table">
            <thead>
              <tr className="t-head-main">
                <th colSpan="3" className="in-side">
                  <FaArrowDown /> MONEY IN
                </th>
                <th colSpan="3" className="out-side">
                  <FaArrowUp /> MONEY OUT
                </th>
              </tr>
              <tr className="t-head-sub">
                <th>BRANCH / DATE</th>
                <th>AMOUNT</th>
                <th>COM</th>
                <th>BRANCH / DATE</th>
                <th>AMOUNT</th>
                <th>COM</th>
              </tr>
            </thead>
            <tbody>
              {selectedParty === "ALL" ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      textAlign: "center",
                      padding: "50px",
                      color: "#666",
                    }}
                  >
                    <strong>Combined Summary Active:</strong> Detailed rows are
                    hidden for performance. Check the totals below for all third
                    parties combined.
                  </td>
                </tr>
              ) : (
                rowIndices.map((idx) => (
                  <tr key={idx} className="data-row">
                    <td className="br-col">
                      {ledgerData.inRows[idx] && (
                        <div className="cell-data">
                          <span>{ledgerData.inRows[idx].branch}</span>
                          <small>
                            {new Date(
                              ledgerData.inRows[idx].date,
                            ).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                            })}
                          </small>
                        </div>
                      )}
                    </td>
                    <td className="amt-col">
                      {ledgerData.inRows[idx]?.amount.toLocaleString() || ""}
                    </td>
                    <td className="com-col">
                      {ledgerData.inRows[idx] && (
                        editingId === ledgerData.inRows[idx].transactionId ? (
                          <div className="tp-comm-edit">
                            <input
                              type="number"
                              defaultValue={ledgerData.inRows[idx].comm}
                              className="tp-comm-input"
                              autoFocus
                              onChange={(e) => setCommissionInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { handleCommissionUpdate(e, ledgerData.inRows[idx].transactionId, ledgerData.inRows[idx].paymentType); setEditingId(null); }
                                if (e.key === "Escape") setEditingId(null);
                              }}
                            />
                            <button className="tp-comm-save" onClick={(e) => { handleCommissionUpdate(e, ledgerData.inRows[idx].transactionId, ledgerData.inRows[idx].paymentType); setEditingId(null); }}>✓</button>
                            <button className="tp-comm-cancel" onClick={() => setEditingId(null)}>✕</button>
                          </div>
                        ) : (
                          <div className="tp-comm-display" onClick={() => { setCommissionInput(ledgerData.inRows[idx].comm); setEditingId(ledgerData.inRows[idx].transactionId); }}>
                            <span className={ledgerData.inRows[idx].comm > 0 ? "tp-comm-val has-val" : "tp-comm-val"}>
                              {ledgerData.inRows[idx].comm > 0 ? ledgerData.inRows[idx].comm.toLocaleString() : "—"}
                            </span>
                            <FaEdit className="tp-comm-icon" />
                          </div>
                        )
                      )}
                    </td>
                    <td className="br-col">
                      {ledgerData.outRows[idx] && (
                        <div className="cell-data">
                          <span>{ledgerData.outRows[idx].branch}</span>
                          <small>
                            {new Date(
                              ledgerData.outRows[idx].date,
                            ).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                            })}
                          </small>
                        </div>
                      )}
                    </td>
                    <td className="amt-col">
                      {ledgerData.outRows[idx]?.amount.toLocaleString() || ""}
                    </td>
                    <td className="com-col">
                      {ledgerData.outRows[idx] && (
                        editingId === ledgerData.outRows[idx].transactionId ? (
                          <div className="tp-comm-edit">
                            <input
                              type="number"
                              defaultValue={ledgerData.outRows[idx].comm}
                              className="tp-comm-input"
                              autoFocus
                              onChange={(e) => setCommissionInput(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") { handleCommissionUpdate(e, ledgerData.outRows[idx].transactionId, ledgerData.outRows[idx].paymentType); setEditingId(null); }
                                if (e.key === "Escape") setEditingId(null);
                              }}
                            />
                            <button className="tp-comm-save" onClick={(e) => { handleCommissionUpdate(e, ledgerData.outRows[idx].transactionId, ledgerData.outRows[idx].paymentType); setEditingId(null); }}>✓</button>
                            <button className="tp-comm-cancel" onClick={() => setEditingId(null)}>✕</button>
                          </div>
                        ) : (
                          <div className="tp-comm-display" onClick={() => { setCommissionInput(ledgerData.outRows[idx].comm); setEditingId(ledgerData.outRows[idx].transactionId); }}>
                            <span className={ledgerData.outRows[idx].comm > 0 ? "tp-comm-val has-val" : "tp-comm-val"}>
                              {ledgerData.outRows[idx].comm > 0 ? ledgerData.outRows[idx].comm.toLocaleString() : "—"}
                            </span>
                            <FaEdit className="tp-comm-icon" />
                          </div>
                        )
                      )}
                    </td>
                  </tr>
                ))
              )}
              <tr className="ledger-footer-total">
                <td>{selectedParty === "ALL" ? "GRAND TOTAL" : "TOTAL"}</td>
                <td>{ledgerData.totalInAmt.toLocaleString()}</td>
                <td>{ledgerData.totalInComm}</td>
                <td></td>
                <td>{ledgerData.totalOutAmt.toLocaleString()}</td>
                <td>{ledgerData.totalOutComm}</td>
              </tr>
            </tbody>
          </table>

          <div className="balance-summary-grid">
            <div className="summary-item yellow">
              <span className="label">OPENING BALANCE (PREV)</span>
              <span className="value">
                ₹{ledgerData.openingBalance.toLocaleString()}
              </span>
            </div>
            <div className="summary-item green">
              <span className="label">TOTAL IN (+)</span>
              <span className="value">
                ₹{ledgerData.totalInAmt.toLocaleString()}
              </span>
            </div>
            <div className="summary-item orange">
              <span className="label">COMMISSION LESS (-)</span>
              <span className="value">
                ₹{ledgerData.totalCommLess.toLocaleString()}
              </span>
            </div>
            <div className="summary-item green">
              <span className="label">TOTAL OUT (-)</span>
              <span className="value">
                ₹{ledgerData.totalOutAmt.toLocaleString()}
              </span>
            </div>
            <div className="summary-item total-balance">
              <span className="label">CLOSING BALANCE</span>
              <span className="value">
                ₹{ledgerData.finalBalance.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
